import React, { useContext } from "react";
import { useQuery } from "@apollo/client";
import useLocalStorage from "../hooks/useLocalStorage";
import { Link } from "react-router-dom";
import {
  Breadcrumb,
  Button,
  Header,
  Icon,
  Loader,
  Card,
  Message,
  Dropdown,
  Menu,
} from "semantic-ui-react";
import StudentCard from "../components/StudentCard";
import AnnouncementsList from "../components/AnnouncementsList";
import { LIST_STUDENTS } from "../GraphQL";
import UserContext from "contexts/UserContext";
import sortBy from "lodash/sortBy";
import { getFirstName, getLastName } from "../utils";

const FILTER_FNS = {
  active: (s) => !s.archived,
  archived: (s) => s.archived,
};

const SORTBY_FNS = {
  first: (s) => getFirstName(s.name),
  last: (s) => getLastName(s.name),
};

const FILTER_OPTIONS = [
  { key: "filter-active", text: "Active Students", value: "active" },
  { key: "filter-archived", text: "Archived Students", value: "archived" },
];

const SORTBY_OPTIONS = [
  { key: "sort-first", text: "First Name", value: "first" },
  { key: "sort-last", text: "Last Name", value: "last" },
];

const ClassPage = () => {
  const user = useContext(UserContext);
  const [filter, setFilter] = useLocalStorage("filter", "active");
  const [sortOption, setSortOption] = useLocalStorage("sortBy", "last");
  const { loading, error, data } = useQuery(LIST_STUDENTS);

  let pageContent = null;

  if (loading) {
    pageContent = <Loader active />;
  } else if (error) {
    throw error;
  } else if (data.myStudents.length === 0) {
    pageContent = (
      <Message info>
        <Message.Header>You have no students in your class!</Message.Header>
        Get started by first{" "}
        <Link to="/settings/fields">
          adding questions to your Question Bank
        </Link>
        , then <Link to="/students/new">add a new student</Link>.
      </Message>
    );
  } else {
    const filterFn = FILTER_FNS[filter];
    const sortByFn = SORTBY_FNS[sortOption];
    const myStudents = sortBy(data.myStudents.filter(filterFn), sortByFn);

    pageContent = (
      <div style={{ marginTop: "1em" }}>
        <Card.Group stackable>
          {myStudents.map((student, i) => (
            <StudentCard key={i} student={student} />
          ))}
        </Card.Group>
      </div>
    );
  }

  const showProfileMessage = user.getAttributes().name === undefined;
  const profileMessage = (
    <Message info icon>
      <Icon name="user circle" />
      <Message.Content>
        <Message.Header>Update your profile</Message.Header>
        Howdy, stranger! Why don't you tell us your{" "}
        <Link to="/settings">name</Link>, and maybe your{" "}
        <Link to="/settings">phone number</Link> to receive text alerts.
      </Message.Content>
    </Message>
  );

  return (
    <div>
      <Breadcrumb size="tiny">
        <Breadcrumb.Section>
          <Link to="/">Home</Link>
        </Breadcrumb.Section>
      </Breadcrumb>
      {showProfileMessage ? profileMessage : null}
      <AnnouncementsList />
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          justifyContent: "space-between",
          borderBottom: "1px solid rgba(34,36,38,.15)",
          paddingBottom: "0.5em",
          marginBottom: "1em",
        }}
      >
        <Header as="h2" style={{ margin: 0 }}>
          My Class
        </Header>
        <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center" }}>
          <Menu text style={{ margin: 0 }}>
            <Menu.Item>
              <span>
                Sort:{" "}
                <Dropdown
                  inline
                  options={SORTBY_OPTIONS}
                  value={sortOption}
                  onChange={(e, { value }) => setSortOption(value)}
                />
              </span>
            </Menu.Item>
            <Menu.Item>
              <span>
                Show:{" "}
                <Dropdown
                  inline
                  options={FILTER_OPTIONS}
                  value={filter}
                  onChange={(e, { value }) => setFilter(value)}
                />
              </span>
            </Menu.Item>
          </Menu>
          <Button basic as={Link} to="/settings/fields">
            <Button.Content>
              <Icon name="question circle" />
              <span className="mobile-hidden"> Question Bank</span>
            </Button.Content>
          </Button>
          {user.canEditStudents ? (
            <Button basic as={Link} to="/students/new">
              <Button.Content>
                <Icon name="user plus" />
                <span className="mobile-hidden"> New Student</span>
              </Button.Content>
            </Button>
          ) : null}
        </div>
      </div>
      {pageContent}
    </div>
  );
};

export default ClassPage;
